<template>
  <v-app v-cloak v-if="!isLoaded">
    <TopNavBar
      @drawerOn="drawerOn"
      @notiDrawerOn="notiDrawerOn"
      :stateBadge="stateBadge"
    />
    <GlobalSnackbar />
    <v-main id="page-wrap">
      <router-view />
    </v-main>
  </v-app>
  <v-app v-else-if="isLoaded">
    <v-sheet
      height="100vh"
      :color="`grey`"
      class="d-flex flex-nowrap justify-center align-center"
    >
      <v-card dark width="30vw" min-width="100px" height="60px">
        <v-card-text>
          Please stand by
          <v-progress-linear
            indeterminate
            color="white"
            class="mb-0"
          ></v-progress-linear>
        </v-card-text>
      </v-card>
    </v-sheet>
  </v-app>
</template>

<script>
import TopNavBar from '@/components/TopNavBar.vue'
import { mapGetters } from 'vuex'
import { Auth } from 'aws-amplify'
import { throttle } from '../lib'
import { tabIds } from '../constants/constant'
import GlobalSnackbar from '@/components/GlobalSnackbar.vue'
import * as Sentry from '@sentry/vue'
import { AuthSync } from '@/utils/authSync'
import { ipfApi } from '@/rest'

export default {
  name: 'MainLayout',
  data() {
    return {
      lastPrice: 125,
      hover: false,
      drawerStatus: null,
      isLoaded: false,
      notiDrawerStatus: null,
      stateBadge: false,
      optionChainOrWatchList: false,
      nextToken: null,
      nextTokenStock: null,
      nextTokenBool: true,
      nextTokenBoolStock: true,
      optionsByUnderlying: [],
      stockList: [],
      inactivityTimeout: null,
      inactivityCheckEvents: [
        'mousemove',
        'keydown',
        'mousedown',
        'touchstart',
      ],
      accessToken: null,
      authSync: null,
    }
  },
  components: {
    GlobalSnackbar,
    TopNavBar,
  },
  created() {
    this.initInactivityTimer()
    const activeTab = localStorage.getItem('activeTab')

    // Initialize auth sync between tabs
    this.authSync = new AuthSync()

    /* NOTE: looking on label because will be a lot of refactoring
        changing activeTab structure to object */
    const tab = Object.values(tabIds).find((tab) => tab.label === activeTab)
    if (tab) this.$store.commit('setActiveTab', tab.id)

    this.connectToSocket()
    this.connectToSocketNotify()
    this.setUpTheme()
    Auth.currentSession()
    Auth.currentAuthenticatedUser({
      bypassCache: true,
    }).then((res) => {
      this.accessToken = res.signInUserSession.idToken.jwtToken

      const newGroup = res
      const groups =
        newGroup.signInUserSession.accessToken.payload['cognito:groups']
      this.$store.commit('setSub', res.attributes.sub)
      this.$store.commit('setUserGroups', groups)
      localStorage.setItem('groups', JSON.stringify(groups))

      this.fetchOptionUnderlyingTable()
      this.fetchStockTable()

      if (groups.includes('newsignups')) {
        this.$router.push('onboarding')
      }
    })
  },
  beforeDestroy() {
    this.closeWS()
    this.$store.commit('resetsubscribedSymbols')

    // Clean up auth sync
    if (this.authSync) {
      this.authSync.close()
    }
  },
  destroyed() {
    this.inactivityCheckEvents.forEach((event) =>
      document.removeEventListener(event, this.resetLogoutTimer)
    )
  },
  computed: {
    ...mapGetters(['getThemes', 'getSelectedTheme']),
    ...mapGetters({ activeTab: 'getActiveTab' }),
  },
  watch: {
    activeTab(newActiveTabValue) {
      if (
        newActiveTabValue == tabIds.optionChain.id ||
        newActiveTabValue == tabIds.watchList.id
      ) {
        this.optionChainOrWatchList = true
      } else {
        this.optionChainOrWatchList = false
      }
    },
  },
  methods: {
    extractValuesAndSort(mappedArr) {
      return mappedArr.map((item) => item.value).sort()
    },
    resetLogoutTimer: throttle(function () {
      const logoutTime = 30 * 60 * 1000 // 30 minutes in milliseconds
      clearTimeout(this.inactivityTimeout)
      this.inactivityTimeout = setTimeout(this.logOut, logoutTime)
    }, 1000), // 1 second
    initInactivityTimer() {
      this.inactivityCheckEvents.forEach((event) =>
        document.addEventListener(event, this.resetLogoutTimer)
      )
      this.resetLogoutTimer()
    },
    drawerOn() {
      this.drawerStatus = true
    },
    drawerOff() {
      this.drawerStatus = false
      this.notiDrawerStatus = false
    },
    notiDrawerOn() {
      this.notiDrawerStatus = true
    },
    noBadge() {
      this.stateBadge = false
    },
    yesBadge() {
      this.stateBadge = true
    },
    // notiDrawerOff() {
    //   this.notiDrawerStatus = false;
    // },
    async logOut() {
      // Notify other tabs before logout
      if (this.authSync) {
        this.authSync.broadcastLogout()
      }

      this.$store.commit('resetProfileState')
      await Auth.signOut()
      Sentry.setUser(null)
      localStorage.clear()

      this.$router.push('/login').catch(() => {})
    },
    async setUpTheme() {
      this.isLoaded = true
      const colorTheme = JSON.parse(localStorage.getItem('colorTheme')) //TODO shift to graphQL
      if (colorTheme) {
        this.$store.commit('setSelectedTheme', colorTheme['selectedTheme'])
        this.$store.commit('setDarkState', colorTheme['darkState'])
        this.$vuetify.theme.dark = colorTheme['darkState']
        const getterName = 'getTheme' + colorTheme['selectedTheme']
        this.$vuetify.theme.themes[colorTheme['darkState'] ? 'dark' : 'light'] =
          this.$store.getters[getterName]
      } else {
        this.$store.commit('setSelectedTheme', 'Soft')
        this.$store.commit('setDarkState', true)
        this.$vuetify.theme.dark = true
        this.$vuetify.theme.themes.dark = this.$store.getters.getThemeSoft
      }
      this.isLoaded = false
    },
    async fetchOptionUnderlyingTable() {
      const distinctOptionExpirarionPairResults =
        await ipfApi.getDistinctOptionExpirationPairs()

      this.optionsByUnderlying =
        distinctOptionExpirarionPairResults.distinct_pairs.map((ele) => [
          ele.underlying,
          ele.autocomplete_weight == 'None'
            ? 0
            : parseInt(ele.autocomplete_weight),
          ele.description,
        ])
      this.optionsByUnderlying?.sort((a, b) => b[1] - a[1])
      const mappedArr = this.optionsByUnderlying?.map((ele) => {
        return {
          value: ele[0] + ' - ' + ele[2],
          // value: ele[0],
          // value: ele[0],
          // key: ele[2],
        }
      })

      if (mappedArr) {
        this.optionsByUnderlying = this.extractValuesAndSort(mappedArr)
        this.$store.commit('setOptionUnderlying', this.optionsByUnderlying)
      }
    },
    async fetchStockTable() {
      const distinctStocksResults = await ipfApi.getDistinctStocks()

      this.stockList = distinctStocksResults?.map((ele) => [
        ele.symbol,
        ele.autocomplete_weight == null ? 0 : parseInt(ele.autocomplete_weight),
        ele.description,
      ])
      this.stockList?.sort((a, b) => b[1] - a[1])
      const mappedArr = this.stockList?.map((ele) => {
        return {
          value: ele[0] + ' - ' + ele[2],
        }
      })

      if (mappedArr) {
        this.stockList = this.extractValuesAndSort(mappedArr)
        this.$store.commit('setListStocks', this.stockList)
      }
    },
  },
}
</script>
<style>
* {
  box-sizing: border-box;
}

html {
  height: 100%;
}

body {
  width: 100%;
  height: 100%;
  /* commented out due to an issue with the onboarding page */
  /* overflow: hidden; */
  margin: 0;
  padding: 0;
}

body::-webkit-scrollbar {
  display: none;
}

.v-main {
  padding: 40px 0px 0px 0px !important;
}
</style>
